import {observer} from 'mobx-react';
import {FC, useCallback, useEffect, useMemo} from 'react';
import {Accordion, Button, Icon, Message} from 'semantic-ui-react';
import {compact, Dictionary, filter, findKey, first, forEach, groupBy, isEmpty, isString, keys,
  map, nth, size, sortBy, uniq} from 'lodash';
import {action} from 'mobx';

import {LAG_MODES, NODE_ROLES, ZONES_ROLE_ORDER} from '../const';
import LeafPeerLinker from './LeafPeerLinker';
import AccessSwitchPeerLinker from './AccessSwitchPeerLinker';
import {speedFromString} from '../utils';
import LinksGroupForm, {labelDefaults, OptionType} from './LinksGroupForm';
import useLinksGroupOptions from '../hooks/useLinksGroupOptions';
import {useRackEditorStore} from '../hooks/useRackEditorStore';

import LinksGroup from '../store/LinksGroup';
import RackEditorStore from '../store/RackEditorStore';
import {SpeedType, SpeedPortsCountType} from '../types';

import './Linker.less';
import LinkGroupSize from './LinksGroupSize';

type LinkerProps = {
  portsSpeeds: Record<string, number>;
  freePorts: SpeedPortsCountType[][];
  newLinkRestriction: string | null ;
}

const Linker: FC<LinkerProps> = ({portsSpeeds, freePorts, newLinkRestriction}) => {
  const {
    isPair, isLeafPair, createLinksGroup, nodesLinksGroups, result, multipleLinks, isDraft,
    onPortClick, portsAvailable, openNewLinksGroupForm, toggleIsRail, trackChanges,
    maxSizePerSpeed, canLinkFirst, canLinkSecond, canLinkBoth, canCreateNewLink,
    showRailOptions, linksGroupOptions, noRailReason, railSpeed
  } = useLinker(portsSpeeds, freePorts);

  if (isPair) {
    // Manage peer links for switch pairs
    return isLeafPair ?
      <LeafPeerLinker portsSpeeds={portsSpeeds} /> :
      <AccessSwitchPeerLinker portsSpeeds={portsSpeeds} />;
  }

  const maxLinks = result.count + (
    multipleLinks ? (portsSpeeds?.[result.speedString] ?? 0) : portsAvailable
  );

  const showNewLinkForm = (canCreateNewLink && !newLinkRestriction) || isDraft;

  return (
    <div className='re-linker'>
      <h5>{'Links Groups:'}</h5>
      <Accordion>
        {
          map(nodesLinksGroups, (linksGroup) =>
            !linksGroup.isDraft &&
              <LinksGroupForm
                key={linksGroup.id}
                linksGroup={linksGroup}
                freePorts={freePorts}
                newLinkRestriction={newLinkRestriction}
              />
          )
        }
        {
          showNewLinkForm &&
            <>
              <Accordion.Title active={isDraft} onClick={openNewLinksGroupForm} className='new-link'>
                <Icon name='dropdown' />
                <b>{`Create ${multipleLinks ? 'Multiple Links Groups' : 'New Links Group'}`}</b>
              </Accordion.Title>
              <Accordion.Content active={isDraft} className='green'>
                <div>
                  <LinkGroupSize
                    showRailOptions={!!showRailOptions}
                    toggleIsRail={toggleIsRail}
                    linksGroup={result}
                    maxSizePerSpeed={maxSizePerSpeed}
                    onPortClick={onPortClick}
                    trackChanges={trackChanges}
                    maxLinks={maxLinks}
                    canLinkFirst={!!canLinkFirst}
                    canLinkSecond={!!canLinkSecond}
                    canLinkBoth={canLinkBoth}
                    linksGroupOptions={linksGroupOptions}
                    noRailReason={noRailReason}
                    railSpeed={railSpeed}
                  >
                    <Button
                      primary
                      size='tiny'
                      disabled={result.count < 1}
                      onClick={createLinksGroup}
                    >
                      {'Create'}
                    </Button>
                  </LinkGroupSize>
                </div>
              </Accordion.Content>
            </>
        }
      </Accordion>
      {
        !showNewLinkForm &&
          <Message warning className='re-linker-errors'>
            {newLinkRestriction}
          </Message>
      }
    </div>
  );
};

export default observer(Linker);

const useLinker = (portsSpeeds, freePorts) => {
  const rackEditorStore: RackEditorStore = useRackEditorStore();
  const {rackStore, selection, multipleLinks, linkNodes, selectedNodes} = rackEditorStore;
  const {linksGroups, hasDraftLinks, draftLinksGroups, openedLinksGroupId, availableRailIndex} = rackStore;

  const nodesInvolved = multipleLinks ? selectedNodes : linkNodes;

  // Links are always oriented from lower zone to the uppers (only makes sense for two nodes selected)
  const [fromNodes, toNodes] = useMemo(
    () => {
      const roleGroups = groupBy(nodesInvolved, (node) => node ? -ZONES_ROLE_ORDER.indexOf(node?.role) : 0);
      return map(sortBy(keys(roleGroups)), (index) => roleGroups[index]);
    },
    [nodesInvolved]
  );
  const [firstNode, secondNode] = [
    first(fromNodes),
    // For the peer links (the same role nodes)
    isEmpty(toNodes) ? nth(fromNodes, 1) : first(toNodes)
  ];

  const result: LinksGroup = openedLinksGroupId ? linksGroups[openedLinksGroupId]! : first(draftLinksGroups)!;

  // This is mostly needed for the single link creation, thus some of the properties will be overridden for
  // multiple links
  const linksGroupOptions = useLinksGroupOptions(result, freePorts);
  const {maxSizePerSpeed, toggleSwitchLink, toggleHoming, onPortClick, portsAvailable, homingType, canCreateNewLink,
    canLinkFirst, canLinkSecond, canLinkBoth, showRailOptions} = linksGroupOptions;

  // For the new links no need to track changes, so, simulate the same behavior by just modifying the result
  const trackChanges = action((change: Dictionary<any>) => {
    forEach(
      hasDraftLinks ? draftLinksGroups : [result],
      (linksGroup) => linksGroup.fillWith(change)
    );
  });

  const setCount = useCallback(
    (count: number) => trackChanges({count}),
    [trackChanges]
  );

  const setSpeed = useCallback(
    (speed: SpeedType | string) => {
      setCount(1);
      trackChanges({speed: isString(speed) ? speedFromString(speed) : speed});
    },
    [trackChanges, setCount]
  );

  const toggleIsRail = () => {
    if (result.isInRail) {
      // Remove from the rail
      trackChanges({railIndex: null});
    } else {
      // Add to a rail
      trackChanges({railIndex: availableRailIndex, count: 1, lagMode: LAG_MODES.NO_LAG});
    }
  };

  const railIndexOptions: OptionType[] = [{text: 'New Index', value: availableRailIndex, label: labelDefaults}];

  useEffect(() => {
    if (hasDraftLinks) {
      // Selects first speed with free ports for the new link
      const firstSpeedString = findKey(portsSpeeds, (count) => (count > 0));
      setSpeed(firstSpeedString ?? null);
      setCount((firstSpeedString && !result.isPeer) ? 1 : 0);
    }
  }, [result]); // eslint-disable-line react-hooks/exhaustive-deps

  // Filter links between the selected nodes
  const compactLinkNodes = compact(linkNodes);
  const nodesLinksGroups = multipleLinks ?
    [] :
    sortBy(
      filter(
        linksGroups, (linkGroup) => linkGroup.contains(compactLinkNodes, true)
      ),
      ['railIndex', 'label']
    );

  const isPair = !multipleLinks && size(linkNodes) === 2 && firstNode?.isPairedWith(secondNode!.id);
  const isLeafPair = !multipleLinks && isPair && firstNode?.role === NODE_ROLES.LEAF;

  const openNewLinksGroupForm = () => !hasDraftLinks && rackStore.toggleLinker(undefined, selection);

  let railSpeed;
  // Check if batch rails creation is possible
  let noRailReason = '';
  if (showRailOptions) {
    if (multipleLinks && toNodes.length > 1) {
      // Not possible (for now) to create rails in bulk
      noRailReason = 'Please select only one Leaf to create a new rail';
    } else if (secondNode?.isPaired) {
      // Not possible to create rails for leaf pairs
      noRailReason = 'Rails cannot be created with Leaf pairs';
    } else {
      const genericsIds = map(fromNodes, 'id');
      const possibleRailSpeeds = uniq(compact(map(genericsIds, (nodeId) => rackStore.railsSpeedsByGenericId[nodeId])));
      const leafRailSpeed = first(possibleRailSpeeds);
      if (result.isInRail) {
        railSpeed = leafRailSpeed;
      } else if (size(possibleRailSpeeds) === 1 && result.speedString !== leafRailSpeed) {
        // If there are rails already created for the given generics, then the speed must be the same for
        // all the rails
        noRailReason = `Selected nodes can only have rails with speed ${leafRailSpeed}`;
      }
    }
  }

  return {
    isPair, isLeafPair, createLinksGroup: rackStore.commitLinksGroup, nodesLinksGroups,
    portsAvailable, homingType, result, openNewLinksGroupForm,
    isDraft: hasDraftLinks, toggleSwitchLink, toggleHoming, canLinkFirst, canLinkSecond, canLinkBoth, canCreateNewLink,
    setSpeed, setCount, multipleLinks,
    maxSizePerSpeed: openedLinksGroupId ? maxSizePerSpeed : portsSpeeds,
    showRailOptions, noRailReason,
    onPortClick: openedLinksGroupId ? onPortClick : setSpeed, railIndexOptions, toggleIsRail, trackChanges,
    linksGroupOptions, railSpeed
  };
};
